import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useNotification } from "@src/context/NotificationContext";
import { useSettings } from "@src/context/SettingsContext";
import {
  useCreatePlaylistMutation,
  useDeletePlaylistMutation,
  useInfinitePlaylists,
  usePlaylistUpdateStatus,
  useUpdatePlaylistMutation,
} from "@src/queries/playlistQueries";
import { IPlaylistRecord } from "@src/types/playlist";
import WrapperContainer from "@components/WrapperContainer";
import ContentContainer from "@src/components/ContentContainer";
import PageTopSection from "@src/components/PageTopSection";
import {
  PlaylistOverviewHeaderLeft,
  PlaylistOverviewHeaderRight,
  BottomButtons,
  ButtonSection,
  TotalLabel,
  BottomButtonsContainer,
  AddActionButton,
  PlaylistOverviewHeaderCenter,
  SearchContainer,
  SearchInputWrapper,
  TotalLabelWrapper,
  MobileTotalLabelWrapper,
  MobileTotalCountContainer,
} from "./Playlists.styles";
import { SearchInput } from "@src/components/SearchInput";
import PageTitle from "@src/components/PageTitle";
import CompactIcon from "@src/assets/img/compacts-icon.svg";
import CompactIconActive from "@src/assets/img/compacts-icon-active.svg";
import FilterIcon from "@src/assets/img/filter_icon.svg";
import FilterIconActive from "@src/assets/img/filter-icon-active.svg";
import PlaylistsList from "@src/components/PlaylistList";
import ErrorLabel from "@src/components/ErrorLabel";
import ContentDrawer from "@src/components/ContentDrawer";
import AddNewPlaylistForm, {
  AddNewPlaylistFormValues,
} from "./AddNewPlaylistForm";
import FilterPlaylistsForm, {
  FilterPlaylistsFormValues,
} from "./FilterPlaylistsForm";
import { useAuthCurator, useAuthState } from "@src/hooks/useAuth";
import useModal from "@src/hooks/useModal";
import DeletePlaylistAlert from "./DeletePlaylistAlert";
import ChangeToReadyAlert from "./ChangeToReadyAlert";
import { CompactActionButton } from "@src/components/CompactActionButton";
import Tooltip from "@src/components/Tooltip";
import { CircleActionButton } from "@src/components/CircleButton";
import { AxiosResponse } from "axios";
import { ApiResponse } from "@src/types/httpApi";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@src/queries/queryKeys";
import {
  getDefaultPlaylistSort,
  resetSort,
  setFilters,
  setFilterDrawerOpen,
  setSort,
} from "@src/redux/slices/playlistSlice";
import { useAppDispatch, useAppSelector } from "@src/redux/hooks";
import { useMyCurators } from "@src/hooks/useMyCurators";
import { TopBarSection } from "@src/components/PageTopSection/PageTopSection";
import debounce from "lodash.debounce";

const schema = yup.object().shape({
  searchText: yup
    .string()
    .max(50, "Search text must be less than 50 characters"),
  statusSelect: yup.string(),
});

export type IPlaylistViewProps = {
  isMoodFactory?: boolean;
};
const Playlists: React.FC<IPlaylistViewProps> = ({ isMoodFactory }) => {
  const routeParams = useParams();
  const [searchParams] = useSearchParams();
  const { role } = useAuthState();
  const curator = useAuthCurator();
  const dispatch = useAppDispatch();
  const filtersApplied = useAppSelector((state) => state.playlists.filters);
  const sorting = useAppSelector((state) => state.playlists.sort);
  const isFilterDrawerOpenFromRedux = useAppSelector(
    (state) => state.playlists.isFilterDrawerOpen
  );
  const [creatingPlaylist, setCreatingPlaylist] = useState<boolean>(false);
  const [successCreatingPlaylists, setSuccessCreatingPlaylists] =
    useState<boolean>(false);
  const [editPlaylistRecord, setEditPlaylistRecord] = useState<
    Partial<AddNewPlaylistFormValues & { documentId: string }> | undefined
  >(undefined);
  const [isAddPlaylistDrawerOpen, setIsAddPlaylistDrawerOpen] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(
    isFilterDrawerOpenFromRedux
  );
  const { addNotification } = useNotification();
  const queryClient = useQueryClient();
  const [deletePlaylistTargetRecord, setDeletePlaylistTargetRecord] = useState<
    IPlaylistRecord | undefined
  >();
  const [changeToReadyTargetRecord, setChangeToReadyTargetRecord] = useState<
    IPlaylistRecord | undefined
  >();
  const { togglePlaylistsCompressUITable, tableSettings } = useSettings();
  const navigate = useNavigate();
  const {
    isOpen: isDeleteModalOpen,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,
    toggleModal: toggleDeleteModal,
  } = useModal();
  const {
    isOpen: isChangeToReadyModalOpen,
    openModal: openChangeToReadyModal,
    closeModal: closeChangeToReadyModal,
    toggleModal: toggleChangeToReadyModal,
  } = useModal();

  useEffect(() => {
    dispatch(setSort(getDefaultPlaylistSort(role?.type)));
  }, [dispatch, role?.type]);

  const showSuccessMessage = (): void => {
    dispatch(resetSort({ role: role?.type }));
    setSuccessCreatingPlaylists(true);
    setTimeout(() => {
      setSuccessCreatingPlaylists(false);
      setIsAddPlaylistDrawerOpen(false);
    }, 1500);
  };
  const {
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      searchText: filtersApplied.searchText || "",
      statusSelect: "",
    },
  });
  const toggleDrawerOpen = useCallback(
    ({
      drawer,
      value,
    }: {
      drawer: "filter" | "add-playlist";
      value: boolean;
    }) => {
      if (drawer === "add-playlist" && !value) {
        setEditPlaylistRecord(undefined);
      }
      if (drawer === "filter") {
        setIsFilterDrawerOpen(value);
        dispatch(setFilterDrawerOpen(value));
      } else {
        setIsAddPlaylistDrawerOpen(drawer === "add-playlist" && value);
      }
    },
    [dispatch]
  );

  const debouncedDispatch = useMemo(
    () =>
      debounce((value: string) => {
        dispatch(setFilters({ searchText: value }));
      }, 300),
    [dispatch]
  );

  const { myCurators } = useMyCurators(curator?.documentId ?? "");
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading: fetchingPlaylists,
    isFetchingNextPage,
    refetch: refetchPlaylists,
  } = useInfinitePlaylists(
    {
      ...filtersApplied,
      isForMood: isMoodFactory,
      sorting,
      subcuratorsIds:
        myCurators?.map((subcurator) => subcurator.documentId) ?? [],
      curatorId: String(curator!.documentId),
      role: role?.type || "curator",
      isSubcurator: curator?.isSubcurator ?? false,
    },
    {
      enabled: !!(
        (curator?.documentId && role?.type === "curator") ||
        role?.type === "admin" ||
        role?.type === "manager"
      ),
    }
  );
  const onRowClick = (row: IPlaylistRecord): void => {
    if (isMoodFactory) {
      return navigate(
        `/mood-factory/${row?.moodFactory?.documentId}/playlist/${row.documentId}`
      );
    }
    navigate(`/playlist/${row.documentId}`);
  };
  const addPlaylistMutation = useCreatePlaylistMutation({
    onSuccess: () => {
      reset();
      setCreatingPlaylist(false);
      refetchPlaylists();
      showSuccessMessage();
    },
    onError: () => {
      setCreatingPlaylist(false);
      addNotification("Failed to create playlist", "error");
    },
  });
  const updatePlaylistMutation = useUpdatePlaylistMutation({
    onSuccess: () => {
      reset();
      setCreatingPlaylist(false);
      showSuccessMessage();
      refetchPlaylists();
    },
    onError: () => {
      setCreatingPlaylist(false);
      addNotification("Failed to update playlist", "error");
    },
  });
  const deletePlaylistMutation = useDeletePlaylistMutation({
    onSuccess: () => {
      showSuccessMessage();
      reset();
      setCreatingPlaylist(false);
      refetchPlaylists();
    },
    onError: () => {
      setCreatingPlaylist(false);
      addNotification("Failed to delete playlist", "error");
    },
  });
  const changeToReadyMutation = usePlaylistUpdateStatus({
    onSuccess: (_data, variables) => {
      refetchPlaylists();
      // The playlist details query is almost always inactive here (we are on
      // the list), and the global config sets `refetchOnMount: false` with a
      // 10min staleTime, so marking it stale is not enough - it would never
      // refetch on mount and the user would keep seeing the old status.
      // `refetchType: "all"` refetches inactive queries too, so the cache is
      // already correct by the time the playlist is opened.
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_PLAYLIST_DETAILS, variables.playlistId],
        refetchType: "all",
      });
      addNotification("Playlist status changed to Ready", "success");
    },
    onError: () => {
      addNotification("Failed to change playlist status", "error");
    },
  });
  const onSubmitNewPlaylist = async (
    data: AddNewPlaylistFormValues
  ): Promise<void> => {
    if (!role?.type)
      return alert("Role type is not defined, please contact support");
    try {
      setCreatingPlaylist(true);
      if (editPlaylistRecord && editPlaylistRecord?.documentId) {
        return updatePlaylistMutation.mutate({
          playlistId: editPlaylistRecord.documentId,
          data: {
            assignedShow: data.assignedShow ?? "",
            notes: data.notes ?? "",
            overwriteShowDescription: data.description ?? null,
            playlistName: data.playlistName,
            curator: data.assignedGuest ?? undefined,
            jingle: data.jingle ?? null,
          },
        });
      } else {
        let curatorId = curator?.documentId || "";
        if (data.assignedGuest) {
          curatorId = data.assignedGuest;
        }
        addPlaylistMutation.mutate({
          assignedShow: data?.assignedShow || "",
          moodFactory: data?.moodFactory || "",
          curator: curatorId,
          notes: data?.notes ?? "",
          playlistName: data?.playlistName,
          overwriteShowDescription: data?.description ?? "",
          jingle: data?.jingle ?? null,
        });
      }
    } catch {
      setCreatingPlaylist(false);
      addNotification("Failed to create playlist", "error");
    }
  };
  const onSubmitFilters = async (
    data: FilterPlaylistsFormValues
  ): Promise<void> => {
    dispatch(
      setFilters({
        showId: data.showId || "",
        playlistName: data.playlistName || "",
        description: data.description || "",
        lastModified: data.lastModified,
        dateAired: data.dateAired,
        durationInMinutes: data.durationInMinutes,
        guestCuratorId: data.guestId || "",
        playlistStatus: data.playlistStatus,
      })
    );
  };

  useEffect(() => {
    if (!isMoodFactory) return;
  }, [routeParams, isMoodFactory]);

  // Deep link from the schedule calendar: /playlists?showId=<documentId> opens
  // the list pre-filtered by that show. The link opens in a new tab, so reading
  // the param once on mount is sufficient — we intentionally don't re-run when
  // searchParams changes to avoid clobbering filters the user sets afterwards.
  useEffect(() => {
    if (isMoodFactory) return;
    const showIdParam = searchParams.get("showId");
    if (showIdParam) {
      dispatch(setFilters({ showId: showIdParam }));
    }
  }, []);

  useEffect(() => {
    setIsFilterDrawerOpen(isFilterDrawerOpenFromRedux);
  }, [isFilterDrawerOpenFromRedux]);

  useEffect(() => {
    const reduxSearchText = filtersApplied.searchText || "";
    reset(
      {
        searchText: reduxSearchText,
        statusSelect: "",
      },
      { keepDefaultValues: true }
    );
  }, []);
  const onPlaylistEditClick = async (
    row: IPlaylistRecord | null
  ): Promise<void> => {
    const editData: Partial<AddNewPlaylistFormValues & { documentId: string }> =
      {
        documentId: row?.documentId ?? "",
        assignedShow: row?.show?.documentId ?? "",
        notes: row?.notes ?? "",
        description: row?.overwriteShowDescription ?? "",
        playlistName: row?.title ?? "",
        assignedGuest: row?.curator?.documentId ?? "",
        jingle: row?.jingle?.documentId ?? "",
      };
    setEditPlaylistRecord((prevData) => {
      if (prevData?.documentId) {
        toggleDrawerOpen({ value: false, drawer: "add-playlist" });
        setTimeout(() => {
          setEditPlaylistRecord(editData);
          toggleDrawerOpen({ value: true, drawer: "add-playlist" });
        }, 300);
        return prevData;
      } else {
        toggleDrawerOpen({ value: true, drawer: "add-playlist" });
        return editData;
      }
    });
  };
  const onDeletePlaylist = useCallback((row: IPlaylistRecord | null): void => {
    if (row) {
      setDeletePlaylistTargetRecord(row);
      openDeleteModal();
    }
  }, []);
  const onConfirmPlaylistDelete = useCallback((): void => {
    if (deletePlaylistTargetRecord) {
      deletePlaylistMutation.mutate(deletePlaylistTargetRecord.documentId);
      closeDeleteModal();
    }
  }, [deletePlaylistTargetRecord]);
  const onChangeToReadyClick = useCallback((row: IPlaylistRecord): void => {
    setChangeToReadyTargetRecord(row);
    openChangeToReadyModal();
  }, []);
  const onConfirmChangeToReady = useCallback((): void => {
    if (changeToReadyTargetRecord) {
      changeToReadyMutation.mutate({
        playlistId: changeToReadyTargetRecord.documentId,
        status: "ready",
      });
      closeChangeToReadyModal();
    }
  }, [changeToReadyTargetRecord]);

  const playlists = useMemo(
    () =>
      (
        (data?.pages as AxiosResponse<ApiResponse<IPlaylistRecord[]>>[]) ?? []
      ).flatMap((response) => response.data.data) || [],
    [data]
  );

  const totalCount = useMemo(
    () =>
      (data?.pages?.[0] as AxiosResponse<ApiResponse<IPlaylistRecord[]>>)?.data
        ?.meta?.pagination?.total ?? "0",
    [data]
  );
  return (
    <WrapperContainer noScroll>
      <PageTopSection>
        <TopBarSection columnCount={3}>
          <PlaylistOverviewHeaderLeft>
            <ButtonSection>
              <PageTitle>{isMoodFactory ? "Moods" : "Playlists"}</PageTitle>
              <BottomButtonsContainer>
                <BottomButtons>
                  <Tooltip content="Create new playlist for your show">
                    <AddActionButton
                      style={{
                        width: 90,
                        height: 40,
                        borderRadius: 50,
                      }}
                      $active={true}
                      onClick={() => {
                        toggleDrawerOpen({
                          value: !isAddPlaylistDrawerOpen,
                          drawer: "add-playlist",
                        });
                      }}
                    >
                      + New
                    </AddActionButton>
                  </Tooltip>
                  <CompactActionButton
                    $tooltipContent="Compact view"
                    $active={tableSettings?.playlistsTable}
                    onClick={togglePlaylistsCompressUITable}
                  >
                    <img
                      src={
                        tableSettings?.playlistsTable
                          ? CompactIconActive
                          : CompactIcon
                      }
                      alt="compact"
                      style={{ width: "50%", objectFit: "contain" }}
                    />
                  </CompactActionButton>
                  {!isMoodFactory && (
                    <CircleActionButton
                      $tooltipContent="Filter"
                      $active={isFilterDrawerOpen}
                      onClick={() => {
                        toggleDrawerOpen({
                          value: !isFilterDrawerOpen,
                          drawer: "filter",
                        });
                      }}
                    >
                      <img
                        src={isFilterDrawerOpen ? FilterIconActive : FilterIcon}
                        alt="filter"
                        style={{ width: "70%", objectFit: "contain" }}
                      />
                    </CircleActionButton>
                  )}
                </BottomButtons>
              </BottomButtonsContainer>
            </ButtonSection>
          </PlaylistOverviewHeaderLeft>
          <PlaylistOverviewHeaderCenter></PlaylistOverviewHeaderCenter>
          <PlaylistOverviewHeaderRight>
            <MobileTotalCountContainer>
              <MobileTotalLabelWrapper>
                <TotalLabel>Total: {totalCount}</TotalLabel>
              </MobileTotalLabelWrapper>
            </MobileTotalCountContainer>
            <SearchContainer>
              <SearchInputWrapper>
                <ErrorLabel>{errors.searchText?.message}</ErrorLabel>
                <Controller
                  name="searchText"
                  control={control}
                  render={({ field }) => (
                    <SearchInput
                      {...field}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        debouncedDispatch(e.target.value || "");
                      }}
                      value={field.value}
                      placeholder="Search by name"
                    />
                  )}
                />
              </SearchInputWrapper>
              <TotalLabelWrapper>
                <TotalLabel>Total: {totalCount}</TotalLabel>
              </TotalLabelWrapper>
            </SearchContainer>
          </PlaylistOverviewHeaderRight>
        </TopBarSection>
      </PageTopSection>
      <ContentContainer allowScroll>
        <DeletePlaylistAlert
          onConfirm={onConfirmPlaylistDelete}
          closeModal={closeDeleteModal}
          isOpen={isDeleteModalOpen}
          openModal={openDeleteModal}
          toggleModal={toggleDeleteModal}
        />
        <ChangeToReadyAlert
          onConfirm={onConfirmChangeToReady}
          isConfirming={changeToReadyMutation.isPending}
          closeModal={closeChangeToReadyModal}
          isOpen={isChangeToReadyModalOpen}
          openModal={openChangeToReadyModal}
          toggleModal={toggleChangeToReadyModal}
        />
        <ContentDrawer
          $floatingDrawer
          isOpen={isAddPlaylistDrawerOpen}
          toggleDrawer={() =>
            toggleDrawerOpen({ value: false, drawer: "add-playlist" })
          }
        >
          <AddNewPlaylistForm
            isMoodFactory={isMoodFactory}
            initialState={editPlaylistRecord}
            creatingPlaylist={creatingPlaylist}
            successCreatingPlaylists={successCreatingPlaylists}
            closeButtonHandler={() =>
              toggleDrawerOpen({ value: false, drawer: "add-playlist" })
            }
            submitButtonHandler={onSubmitNewPlaylist}
          />
        </ContentDrawer>
        {!isMoodFactory && (
          <ContentDrawer
            isOpen={isFilterDrawerOpen}
            toggleDrawer={() =>
              toggleDrawerOpen({ value: false, drawer: "filter" })
            }
          >
            <FilterPlaylistsForm
              fetchingPlaylist={fetchingPlaylists}
              closeButtonHandler={() =>
                toggleDrawerOpen({ value: false, drawer: "filter" })
              }
              submitButtonHandler={onSubmitFilters}
              isOpen={isFilterDrawerOpen}
            />
          </ContentDrawer>
        )}
        <PlaylistsList
          isMoodFactory={isMoodFactory}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          fetchingPlaylists={fetchingPlaylists}
          playlists={playlists}
          onDeleteClick={onDeletePlaylist}
          onEditClick={onPlaylistEditClick}
          onRowClick={onRowClick}
          onChangeToReadyClick={onChangeToReadyClick}
        />
      </ContentContainer>
    </WrapperContainer>
  );
};

export default Playlists;
